import './App.css';
import './own.css';
import React, {useEffect} from "react";
import Login from "./components/Login/Login";
import Dashboard from "./components/Dashboard/Dashboard";
import ReactDOM from "react-dom/client";
import {
    BrowserRouter,
    Routes,
    Route, Navigate,
} from "react-router-dom";

import {ProtectedRoute} from "./components/ProtectedRoute/ProtectedRoute";

import {useDispatch, useSelector} from "react-redux";
import {NoLoggedRoute} from "./components/NoLoggedRoute/NoLoggedRoute";
import {check} from "./features/users/Users";
import {Backdrop, Box, CircularProgress, Container, Grid, Paper, Stack} from "@mui/material";
import SupplyFeeds from "./components/Feeds/SupplyFeeds";
import DemandFeeds from "./components/Feeds/DemandFeeds";
import {getStats, init, isLoading, ready} from "./features/app/App";
import store from "./store/store";
import {logged} from "./features/users/usersSlice";
import Users from "./components/Users/Users";
import DemandFeedsClosing from "./components/Invoices/DemandFeedsClosing";
import Invoices from "./components/Invoices/Invoices";
import DemandStats from "./components/Admin/DemandStats";
import SupplyDemand from "./components/Feeds/SupplyDemand";
import SupplyAdmin from "./components/Admin/SupplyAdmin";
import BillingInfo from "./components/Users/BillingInfo";
import Settings from "./components/Users/Settings";
import DemandSupply from "./components/Admin/DemandSupply";
import FeedAlerts from "./components/Admin/FeedAlerts";
import Mediabuying from "./components/Admin/Mediabuying";
import FeedPerformance from "./components/Admin/FeedPerformance";
import Conversions from "./components/Admin/Conversions";
import TrafficControl from "./components/Admin/TrafficControl";
import CreateSupplyFeedPanel from "./components/Spin/CreateSupplyFeedPanel";
import FeedList from "./components/Spin/FeedList";
import KeywordPanel from "./components/Keywords/KeywordPanel";
import MediaBuyingReportPanel from "./components/Admin/MediaBuyingReportPanel";
import BlacklistManager from "./components/Admin/BlacklistManager";
import RotatorsPanel from "./components/Admin/RotatorsPanel";
import { RsocGenerator, RsocPagesList, RsocAnalytics, ParkingEvents, LauncherPage, RsocDiscovery, RsocRules, RecommendedArticles, RsocInspiration } from "./components/RSOC";

function App() {
    const userLogged = useSelector((state) => state.users.logged);
    const isAppLoading = useSelector((state) => state.app.isLoading);
    const userIsAdmin = useSelector((state) => state.users.is_admin); // Admin status
    const userRole = useSelector((state) => state.users.role); // Role of user: "demand" or "supply"

    useEffect(() => {
        init().then(() => {});
    }, []);

    // Show loading spinner until user role is defined
    if (isAppLoading || userLogged == null ) {
        return (
            <React.Fragment>
                <Backdrop
                    sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
                    open={true}
                >
                    <CircularProgress color="inherit" />
                </Backdrop>
            </React.Fragment>
        );
    }

    return (
        <React.Fragment>
            <BrowserRouter>
                <Routes>
                    {/* Protected Routes */}
                    <Route exact path="/" element={<ProtectedRoute />}>
                        {/* Demand User Routes */}
                        {userRole === 'demand' && !userIsAdmin && (
                            <>
                                <Route exact path="/" element={<Navigate to="/spin/list" />} /> {/* Redirect to create feed */}
                                <Route exact path="/spin/create" element={<CreateSupplyFeedPanel />} />
                                <Route exact path="/spin/list" element={<FeedList />} />
                                <Route path="/mediabuying/keywords" element={<KeywordPanel />} />
                                <Route exact path="/mediabuying/dashboard" element={<MediaBuyingReportPanel />} />
                                <Route exact path="/admin/blacklist" element={<BlacklistManager />} />
                                <Route exact path="/admin/rotators" element={<RotatorsPanel />} />
                                <Route exact path="/rsoc/generator" element={<RsocGenerator />} />
                                <Route exact path="/rsoc/pages" element={<RsocPagesList />} />
                                <Route exact path="/rsoc/analytics" element={<RsocAnalytics />} />
                                <Route exact path="/rsoc/parking-events" element={<ParkingEvents />} />
                                <Route exact path="/rsoc/launcher" element={<LauncherPage />} />
                                <Route exact path="/rsoc/discovery" element={<RsocDiscovery />} />
                                <Route exact path="/rsoc/rules" element={<RsocRules />} />
                                <Route exact path="/rsoc/recommended-articles" element={<RecommendedArticles />} />
                                <Route exact path="/rsoc/inspiration" element={<RsocInspiration />} />

                                <Route path="*" element={<Navigate to="/spin/list" />} /> {/* Redirect invalid routes */}
                                
                            </>
                        )}

                        {/* Supply User Routes */}
                        {userRole === 'supply' && !userIsAdmin && (
                            <>
                                <Route exact path="/" element={<Dashboard />} />
                                <Route exact path="/supply/feeds" element={<SupplyFeeds />} />
                                <Route exact path="/billing" element={<BillingInfo />} />
                                <Route exact path="/invoices" element={<Invoices />} />
                                <Route exact path="/user/settings" element={<Settings />} />
                                <Route exact path="/admin/supply" element={<SupplyAdmin />} />
                                <Route exact path="/admin/blacklist" element={<BlacklistManager />} />
                                <Route path="*" element={<Navigate to="/" />} />
                            </>
                        )}

                        {/* Admin Routes (Access to All) */}
                        {userIsAdmin && (
                            <>
                                <Route exact path="/" element={<Dashboard />} />
                                <Route exact path="/supply/feeds" element={<SupplyFeeds />} />
                                <Route exact path="/billing" element={<BillingInfo />} />
                                <Route exact path="/user/settings" element={<Settings />} />
                                <Route exact path="/feeds/create" element={<CreateSupplyFeedPanel />} />
                                <Route exact path="/demand/feeds" element={<DemandFeeds />} />
                                <Route exact path="/invoices" element={<Invoices />} />
                                <Route exact path="/invoices/closing" element={<DemandFeedsClosing />} />
                                <Route exact path="/demand/stats" element={<DemandStats />} />
                                <Route exact path="/supplydemand" element={<SupplyDemand />} />
                                <Route exact path="/users" element={<Users />} />
                                <Route exact path="/admin/alerts" element={<FeedAlerts />} />
                                <Route exact path="/admin/supply" element={<SupplyAdmin />} />
                                <Route exact path="/admin/feeds" element={<DemandSupply />} />
                                <Route exact path="/admin/mediabuying" element={<Mediabuying />} />
                                <Route exact path="/admin/feed-performance" element={<FeedPerformance />} />
                                <Route exact path="/admin/traffic-control" element={<TrafficControl />} />
                                <Route exact path="/admin/conversions" element={<Conversions />} />
                                <Route exact path="/admin/keywords" element={<KeywordPanel />} />
                                <Route exact path="/admin/blacklist" element={<BlacklistManager />} />
                                <Route exact path="/admin/rotators" element={<RotatorsPanel />} />
                                <Route exact path="/rsoc/generator" element={<RsocGenerator />} />
                                <Route exact path="/rsoc/pages" element={<RsocPagesList />} />
                                <Route exact path="/rsoc/analytics" element={<RsocAnalytics />} />
                                <Route exact path="/rsoc/parking-events" element={<ParkingEvents />} />
                                <Route exact path="/rsoc/launcher" element={<LauncherPage />} />
                                <Route exact path="/rsoc/discovery" element={<RsocDiscovery />} />
                                <Route exact path="/rsoc/rules" element={<RsocRules />} />
                                <Route exact path="/rsoc/recommended-articles" element={<RecommendedArticles />} />
                                <Route exact path="/rsoc/inspiration" element={<RsocInspiration />} />
                                <Route exact path="/spin/list" element={<FeedList />} />
                                <Route exact path="/spin/create" element={<CreateSupplyFeedPanel />} />
                                <Route path="/mediabuying/keywords" element={<KeywordPanel />} />
                                <Route path="/mediabuying/dashboard" element={<MediaBuyingReportPanel />} />
                                <Route path="*" element={<Navigate to="/" />} />
                            </>
                        )}
                    </Route>

                    {/* NoLoggedRoute for Unauthenticated Users */}
                    <Route exact path="/" element={<NoLoggedRoute />}>
                        <Route exact path="/login" element={<Login />} />
                        <Route path="*" element={<Navigate to="/login" />} />

                    </Route>
                </Routes>
            </BrowserRouter>
        </React.Fragment>
    );
}

export default App;